// Îlots de marché emportés sur l'appareil (D-303, D-305) : îlot et places dans l'ordre rangée, rang,
// téléchargés avec une connexion puis recensés hors connexion. Les saisies de l'agent sont notées
// localement pour l'avancement affiché ; le serveur reste seul juge (doublons, conflits). Purgés avec
// le travail local à chaque changement de compte ou de collectivité.
import type { Coord, Numerotation } from '@cc/contrats';
import { requete } from '../api/client';
import { ouvrirBase } from './stockage';

export const CLE_ILOTS = 'ilots-marches';

export interface PlaceIlot {
  id: string;
  reference: string;
  rangee: number | null;
  rang: number | null;
  position_version: number;
  position: { longitude: number; latitude: number } | null;
  recensement: { statut: string; occupant: string | null; activite: string | null } | null;
  occupant_attribue: string | null;
}

export interface FicheIlot {
  id: string;
  marche_id: string;
  code: string;
  nom: string;
  rangees: number;
  places_par_rangee: number;
  numerotation: Numerotation;
  precision: 'schematique' | 'terrain';
  version: number;
  coins: Coord[];
  places: PlaceIlot[];
}

export interface SaisieLocale {
  statut: 'occupee' | 'libre';
  occupant: string | null;
  enregistree_le: string;
}

export interface IlotEmporte {
  ilot: Omit<FicheIlot, 'places'>;
  places: PlaceIlot[];
  saisies: Record<string, SaisieLocale>;
  telecharge_le: string;
}

async function tous(): Promise<Record<string, IlotEmporte>> {
  return (
    ((await (await ouvrirBase()).get('meta', CLE_ILOTS)) as
      Record<string, IlotEmporte> | undefined) ?? {}
  );
}

async function ecrire(
  id: string,
  maj: (actuel: IlotEmporte | undefined) => IlotEmporte | undefined,
) {
  const b = await ouvrirBase();
  const tx = b.transaction('meta', 'readwrite');
  const liste = ((await tx.store.get(CLE_ILOTS)) as Record<string, IlotEmporte> | undefined) ?? {};
  const suivant = maj(liste[id]);
  if (suivant) await tx.store.put({ ...liste, [id]: suivant }, CLE_ILOTS);
  await tx.done;
}

export async function ilotsEmportes(): Promise<IlotEmporte[]> {
  return Object.values(await tous()).sort((a, b) => a.ilot.code.localeCompare(b.ilot.code));
}

export async function ilotEmporte(id: string): Promise<IlotEmporte | null> {
  return (await tous())[id] ?? null;
}

/** Télécharge l'îlot et ses places ; les saisies locales déjà connues du serveur sont oubliées. */
export async function telechargerIlot(id: string): Promise<IlotEmporte> {
  const { places, ...ilot } = await requete<FicheIlot>(`/marches/ilots/${id}`);
  const emporte: IlotEmporte = {
    ilot,
    places,
    saisies: {},
    telecharge_le: new Date().toISOString(),
  };
  await ecrire(id, () => emporte);
  return emporte;
}

export async function noterSaisie(
  ilotId: string,
  placeId: string,
  saisie: SaisieLocale,
): Promise<void> {
  await ecrire(ilotId, (a) =>
    a ? { ...a, saisies: { ...a.saisies, [placeId]: saisie } } : undefined,
  );
}

/** Avancement local : places recensées (serveur ou saisie de l'appareil) sur l'ensemble. */
export function avancementLocal(e: IlotEmporte): { recensees: number; places: number } {
  return {
    places: e.places.length,
    recensees: e.places.filter((p) => p.recensement || e.saisies[p.id]).length,
  };
}
